import { getThaalam } from '@swarasense/shared';
import type { NotationTimeline } from '@swarasense/shared';
import { beatToX, buildTicks, currentNoteIndex, PX_PER_BEAT } from './notation-lane-geometry.js';

/**
 * Task 2 (targeted drills): renders a `NotationTimeline` — a drill today,
 * exercises/songs later — laid out on the thaalam beat grid, with a
 * follow-along cursor the practice flow (`app.ts`) advances from the audio/
 * beat clock while the student sings. Reused as-is by whatever later mode
 * plays a `NotationTimeline`; this component owns only the rendering, never
 * the transport/metronome/recording it sits above.
 *
 * Deliberately DOM-based (not Canvas, unlike the pitch ribbon): a handful of
 * discrete swara tokens on a beat grid is exactly what `<div>`s position
 * naturally, and it keeps the accessible fallback (the visually-hidden
 * `<table>` below) trivially in sync with what's drawn — no separate
 * hit-testing/geometry duplication the way a canvas label would need.
 */
export class NotationLane {
  private readonly root: HTMLElement;
  private readonly figure: HTMLElement;
  private readonly track: HTMLElement;
  private readonly ticksLayer: HTMLElement;
  private readonly notesLayer: HTMLElement;
  private readonly cursorEl: HTMLElement;
  private readonly tableBody: HTMLElement;
  private readonly reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  private timeline: NotationTimeline | undefined;
  private noteEls: HTMLElement[] = [];
  private currentIdx = -1;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'notation-lane';

    this.figure = document.createElement('div');
    this.figure.className = 'notation-figure';
    this.figure.setAttribute('role', 'img');
    this.figure.setAttribute('aria-label', 'Practice phrase notation: none loaded');

    const scroller = document.createElement('div');
    scroller.className = 'notation-scroll';
    // The tick marks and note tokens are the VISUAL rendering the parent's
    // `role="img"`/`aria-label` already summarizes; some assistive tech
    // walks into a role=img's subtree anyway and reads each note token's
    // raw text (just a bare swara letter, no context) on top of — or
    // instead of — the figure's own label. `aria-hidden` here makes the
    // sr-only `<table>` fallback below the one and only accessible source
    // for per-note detail, matching the figure's `aria-label`'s summary.
    scroller.setAttribute('aria-hidden', 'true');

    this.track = document.createElement('div');
    this.track.className = 'notation-track';

    this.ticksLayer = document.createElement('div');
    this.ticksLayer.className = 'notation-ticks';
    this.notesLayer = document.createElement('div');
    this.notesLayer.className = 'notation-notes';
    this.cursorEl = document.createElement('div');
    this.cursorEl.className = 'notation-cursor';
    this.cursorEl.setAttribute('aria-hidden', 'true');

    this.track.append(this.ticksLayer, this.notesLayer, this.cursorEl);
    scroller.appendChild(this.track);
    this.figure.appendChild(scroller);

    // Accessible fallback: a real `<table>` of swara×beat, visually hidden
    // (`.sr-only`) but reachable by a screen reader — the same "canvas/visual
    // has a role=img summary, a table carries the actual per-item detail"
    // split `results-panel.ts`'s heatmap uses.
    const tableWrap = document.createElement('div');
    tableWrap.className = 'sr-only';
    const table = document.createElement('table');
    table.className = 'notation-table';
    const caption = document.createElement('caption');
    caption.textContent = 'Practice phrase: swara by beat';
    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    const beatTh = document.createElement('th');
    beatTh.scope = 'col';
    beatTh.textContent = 'Beat';
    const swaraTh = document.createElement('th');
    swaraTh.scope = 'col';
    swaraTh.textContent = 'Swara';
    headRow.append(beatTh, swaraTh);
    thead.appendChild(headRow);
    this.tableBody = document.createElement('tbody');
    table.append(caption, thead, this.tableBody);
    tableWrap.appendChild(table);

    this.root.append(this.figure, tableWrap);
    container.appendChild(this.root);
  }

  /** Loads a new timeline and (re)draws the static grid — ticks, note tokens,
   * the table fallback, and the `role="img"` summary — then resets the
   * cursor to beat 0. */
  setTimeline(tl: NotationTimeline): void {
    this.timeline = tl;
    this.currentIdx = -1;
    this.renderStatic();
    this.setCursorBeat(0);
  }

  private renderStatic(): void {
    this.ticksLayer.replaceChildren();
    this.notesLayer.replaceChildren();
    this.tableBody.replaceChildren();
    this.noteEls = [];

    const tl = this.timeline;
    if (!tl) {
      this.figure.setAttribute('aria-label', 'Practice phrase notation: none loaded');
      this.track.style.width = '0px';
      return;
    }

    const thaalam = getThaalam(tl.thaalam);
    const ticks = buildTicks(thaalam, tl.totalBeats);
    this.track.style.width = `${Math.max(beatToX(tl.totalBeats), PX_PER_BEAT)}px`;

    for (const tick of ticks) {
      const el = document.createElement('div');
      el.className = `notation-tick notation-tick--${tick.type}`;
      el.style.left = `${beatToX(tick.beat)}px`;
      this.ticksLayer.appendChild(el);
    }

    const NOTE_GUTTER_PX = 3;
    for (const note of tl.notes) {
      const el = document.createElement('div');
      el.className = 'notation-note';
      el.style.left = `${beatToX(note.startBeat) + NOTE_GUTTER_PX}px`;
      el.style.width = `${Math.max(beatToX(note.durationBeats) - 2 * NOTE_GUTTER_PX, 0)}px`;
      el.textContent = swaraLabel(note.swara, note.octave);
      this.notesLayer.appendChild(el);
      this.noteEls.push(el);

      const row = document.createElement('tr');
      const beatTd = document.createElement('td');
      beatTd.textContent = String(note.startBeat + 1); // 1-based for a human reader
      const swaraTd = document.createElement('td');
      swaraTd.textContent = `${swaraLabel(note.swara, note.octave)} (${octaveWord(note.octave)})`;
      row.append(beatTd, swaraTd);
      this.tableBody.appendChild(row);
    }

    const phrase = tl.notes.map((n) => swaraLabel(n.swara, n.octave)).join(' ');
    this.figure.setAttribute(
      'aria-label',
      `Practice phrase in ${tl.raga}, ${tl.thaalam} thaalam at ${tl.tempoBpm} BPM: ${phrase}`,
    );
  }

  /**
   * Moves the cursor to `beat` (clamped to the timeline's span) and
   * highlights whichever note is active there. Under
   * `prefers-reduced-motion`, the cursor doesn't glide continuously between
   * notes — it snaps straight to the CURRENT note's start, so it only ever
   * "steps" at note boundaries, never mid-note motion (this is enforced in
   * JS, not just left to the page-wide reduced-motion CSS rule that zeroes
   * transition durations, so it holds even for a caller that polls this
   * method at a very coarse interval).
   */
  setCursorBeat(beat: number): void {
    const tl = this.timeline;
    if (!tl) return;
    const clamped = Math.max(0, Math.min(beat, tl.totalBeats));
    const idx = currentNoteIndex(tl.notes, clamped);
    const reduced = this.reducedMotionQuery.matches;
    const positionBeat = reduced && idx >= 0 ? tl.notes[idx]!.startBeat : clamped;
    this.cursorEl.style.transform = `translateX(${beatToX(positionBeat)}px)`;
    if (idx !== this.currentIdx) this.highlightNote(idx);
  }

  /** Marks note `i` as the current target (accent fill); clears any other
   * note's highlight. `i` outside the loaded timeline's range (e.g. `-1`)
   * just clears every highlight, so callers never need to guard the index. */
  highlightNote(i: number): void {
    for (const el of this.noteEls) el.classList.remove('current');
    const el = this.noteEls[i];
    if (el) el.classList.add('current');
    this.currentIdx = i;
  }

  /** Empties the lane back to "nothing loaded" (e.g. leaving practice mode). */
  clear(): void {
    this.timeline = undefined;
    this.currentIdx = -1;
    this.renderStatic();
    this.cursorEl.style.transform = 'translateX(0px)';
  }

  destroy(): void {
    this.root.remove();
  }
}

/** Carnatic octave marks: a combining dot above (tara sthayi) or below
 * (mandra sthayi) the swara letter — the general form of the precomposed 'Ṡ'
 * the pitch ribbon already draws for the octave Sa (`ribbon.ts`). A drill's
 * neighbour notes can land an octave up/down on ANY swara, not just Sa, and
 * there's no precomposed Latin-plus-dot glyph for most of them, so this uses
 * the actual Unicode combining marks instead. Exported: Task 3's gentle
 * per-note review (`practice.ts`) reuses this for its own note chips instead
 * of re-deriving the same octave-mark convention. */
export function swaraLabel(swara: string, octave: -1 | 0 | 1): string {
  if (octave === 1) return `${swara}̇`; // combining dot above
  if (octave === -1) return `${swara}̣`; // combining dot below
  return swara;
}

function octaveWord(octave: -1 | 0 | 1): string {
  return octave === 1 ? 'tara sthayi' : octave === -1 ? 'mandra sthayi' : 'madhya sthayi';
}
